import type { StatTestType } from '../../../../dashboards/data-generator/test-generator/test-library-datasets.model';
import type { DistributionType, TestResult } from '../../tests/test-runner/test-runner.model';

// ---------------------------------------------------------------------------
// Test measure row (test statistic, df, critical value, p-value)
// ---------------------------------------------------------------------------

export type TestMeasureId = 'testStatistic' | 'degreesOfFreedom' | 'criticalValue' | 'pValue';

export interface TestMeasureDef {
  readonly id: TestMeasureId;
  readonly label: string;
  readonly symbolLatex: string;
  readonly description: string;
  readonly formulaLatex: string;
}

export const TEST_MEASURE_COLORS: Readonly<Record<TestMeasureId, string>> = {
  testStatistic: '#0ea5e9',
  degreesOfFreedom: '#9ca3af',
  criticalValue: '#ef4444',
  pValue: '#8b5cf6',
};

// ---------------------------------------------------------------------------
// Sample statistic row (x̄, s, n, etc.)
// ---------------------------------------------------------------------------

export interface SampleStatDef {
  readonly key: string;
  readonly symbolLatex: string;
  readonly label: string;
  readonly description: string;
}

export const SAMPLE_STAT_COLOR = '#06b6d4';

/** Maps unicode sample-stat keys to KaTeX symbols. */
export const STAT_KEY_TO_LATEX: Readonly<Record<string, string>> = {
  'x\u0304': '\\bar{x}',
  s: 's',
  n: 'n',
  'x\u0304\u2081': '\\bar{x}_1',
  'x\u0304\u2082': '\\bar{x}_2',
  's\u2081': 's_1',
  's\u2082': 's_2',
  'n\u2081': 'n_1',
  'n\u2082': 'n_2',
  'd\u0304': '\\bar{d}',
  s_d: 's_d',
  'S+': 'S^+',
  'S\u2212': 'S^-',
  N: 'N',
  k: 'k',
  E: 'E',
  df: '\\text{df}',
};

/** Maps unicode sample-stat keys to human-readable labels. */
const SAMPLE_STAT_LABELS: Readonly<Record<string, string>> = {
  'x\u0304': 'Sample Mean',
  s: 'Sample Std. Dev.',
  n: 'Sample Size',
  'x\u0304\u2081': 'Mean (Group 1)',
  'x\u0304\u2082': 'Mean (Group 2)',
  's\u2081': 'Std. Dev. (Group 1)',
  's\u2082': 'Std. Dev. (Group 2)',
  'n\u2081': 'Size (Group 1)',
  'n\u2082': 'Size (Group 2)',
  'd\u0304': 'Mean Difference',
  s_d: 'Std. Dev. of Differences',
  'S+': 'Positive Signs',
  'S\u2212': 'Negative Signs',
  N: 'Total Count',
  k: 'Categories',
  E: 'Expected Frequency',
  df: 'Degrees of Freedom',
};

/** Maps unicode sample-stat keys to tooltip descriptions. */
const SAMPLE_STAT_DESCRIPTIONS: Readonly<Record<string, string>> = {
  'x\u0304': 'The average of all sample values.',
  s: 'Measures the spread of values around the mean.',
  n: 'The number of observations in the sample.',
  'x\u0304\u2081': 'The average of group 1 values.',
  'x\u0304\u2082': 'The average of group 2 values.',
  's\u2081': 'Spread of values in group 1.',
  's\u2082': 'Spread of values in group 2.',
  'n\u2081': 'Number of observations in group 1.',
  'n\u2082': 'Number of observations in group 2.',
  'd\u0304': 'Average of the paired differences.',
  s_d: 'Spread of the paired differences.',
  'S+': 'Number of positive differences (excluding ties).',
  'S\u2212': 'Number of negative differences (excluding ties).',
  N: 'Grand total of all observations.',
  k: 'Number of categories.',
  E: 'Expected frequency under the null hypothesis.',
  df: 'Number of independent values that can vary.',
};

// ---------------------------------------------------------------------------
// Test measure definitions per distribution type
// ---------------------------------------------------------------------------

const T_STAT_DEF: TestMeasureDef = {
  id: 'testStatistic',
  label: 'Test Statistic',
  symbolLatex: '$t$',
  description: 'How many standard errors the sample mean is from the null hypothesis value.',
  formulaLatex: '$t = \\dfrac{\\bar{x} - \\mu_0}{s / \\sqrt{n}}$',
};

const CHI_STAT_DEF: TestMeasureDef = {
  id: 'testStatistic',
  label: 'Test Statistic',
  symbolLatex: '$\\chi^2$',
  description: 'Measures the discrepancy between observed and expected frequencies.',
  formulaLatex: '$\\chi^2 = \\sum \\dfrac{(O_i - E_i)^2}{E_i}$',
};

const SIGN_STAT_DEF: TestMeasureDef = {
  id: 'testStatistic',
  label: 'Test Statistic',
  symbolLatex: '$S^+$',
  description: 'The number of positive differences in the sign test.',
  formulaLatex: '$S^+ = \\text{count of positive differences}$',
};

function getTestStatDef(distributionType: DistributionType): TestMeasureDef {
  if (distributionType === 't') {
    return T_STAT_DEF;
  }
  if (distributionType === 'chi-squared') {
    return CHI_STAT_DEF;
  }
  return SIGN_STAT_DEF;
}

function getDfDef(result: TestResult): TestMeasureDef {
  const formulaMap: Partial<Record<StatTestType, string>> = {
    'one-sample-t': '$\\text{df} = n - 1$',
    'paired-t': '$\\text{df} = n - 1$',
    'two-sample-t': '$\\text{df} = \\text{Welch–Satterthwaite}$',
    'chi-squared-independence': '$\\text{df} = (r - 1)(c - 1)$',
    'chi-squared-gof': '$\\text{df} = k - 1$',
  };

  return {
    id: 'degreesOfFreedom',
    label: 'Degrees of Freedom',
    symbolLatex: '$\\text{df}$',
    description: 'The number of independent values that can vary in the test.',
    formulaLatex: formulaMap[result.testType] ?? '$\\text{df}$',
  };
}

function getCriticalValueDef(distributionType: DistributionType): TestMeasureDef {
  const symbol =
    distributionType === 't' ? '$t^*$' : distributionType === 'chi-squared' ? '$\\chi^2_{\\text{crit}}$' : '$c$';
  return {
    id: 'criticalValue',
    label: 'Critical Value',
    symbolLatex: symbol,
    description: 'The boundary value that separates the rejection and acceptance regions.',
    formulaLatex: '',
  };
}

const P_VALUE_DEF: TestMeasureDef = {
  id: 'pValue',
  label: 'p-value',
  symbolLatex: '$p$',
  description: 'The probability of observing a test statistic as extreme as the one computed, assuming H₀ is true.',
  formulaLatex: '',
};

/** Returns the test-measure definitions for a given test result. */
export function getTestMeasureDefs(result: TestResult): TestMeasureDef[] {
  const defs: TestMeasureDef[] = [getTestStatDef(result.distributionType)];

  // p-value right after test statistic — it's directly derived from it
  defs.push(P_VALUE_DEF);
  defs.push(getCriticalValueDef(result.distributionType));

  if (result.distributionType !== 'binomial') {
    defs.push(getDfDef(result));
  }

  return defs;
}

/** Returns sample stat definitions from the result's sampleStats keys. */
export function getSampleStatDefs(result: TestResult): SampleStatDef[] {
  return Object.keys(result.sampleStats)
    .filter((key) => key !== 'df') // df is shown in test results section
    .map((key) => ({
      key,
      symbolLatex: `$${STAT_KEY_TO_LATEX[key] ?? key}$`,
      label: SAMPLE_STAT_LABELS[key] ?? key,
      description: SAMPLE_STAT_DESCRIPTIONS[key] ?? '',
    }));
}
